import React, { useEffect, useState } from "react";
import { Alert, Button, Empty, Popconfirm, Select, Space, Spin, Table, Tag, message } from "antd";
import { ExperimentOutlined, PlusOutlined } from "@ant-design/icons";
import { getFacilityListApi } from "../../../../../services/facilities.services";
import {
  cancelBillingPolicyApi,
  getBillingPoliciesApi,
} from "../../../../../services/billingPolicies.services";
import PolicyEditorModal from "./PolicyEditorModal";
import { MODE_LABELS, MODE_TABLES, apiError, describeTiers, formatDateTime } from "./policyUtils";
import styles from "./BillingPolicies.module.css";

const StatusTag = ({ policy }) => {
  if (policy.status === "SCHEDULED") return <Tag color="blue">Scheduled</Tag>;
  if (policy.status === "CANCELLED") return <Tag>Cancelled</Tag>;
  return policy.is_current ? <Tag color="green">In force</Tag> : <Tag color="default">Superseded</Tag>;
};

const PolicyRules = ({ policy }) => {
  const tables = MODE_TABLES[policy.billing_mode] || [];
  if (!tables.length) return <div className={styles.muted}>3h–6h leftover: half day · 6h or more: full day</div>;
  return (
    <div>
      {tables.map((t) => (
        <div key={t.key} className={styles.ruleLine}>
          <span className={styles.ruleName}>{t.label}</span>
          <span>{describeTiers(policy.configuration?.[t.key])}</span>
        </div>
      ))}
    </div>
  );
};

const BillingPolicies = () => {
  const [facilities, setFacilities] = useState([]);
  const [facilityId, setFacilityId] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editor, setEditor] = useState({ open: false, mode: "create", policy: null });

  useEffect(() => {
    getFacilityListApi()
      .then((res) => {
        const list = Array.isArray(res?.data) ? res.data : res?.data?.results || res?.data?.data || [];
        setFacilities(list.map((f) => ({ value: f.id, label: f.name })));
        if (list.length) setFacilityId(list[0].id);
      })
      .catch(() => message.error("Failed to load facilities"));
  }, []);

  const load = async (id = facilityId) => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await getBillingPoliciesApi(id);
      setData(res?.data?.data || null);
    } catch (e) {
      setData(null);
      message.error(apiError(e, "Failed to load billing policies"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(facilityId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId]);

  const cancelPolicy = async (policy) => {
    try {
      await cancelBillingPolicyApi(policy.id);
      message.success(`Version ${policy.version} cancelled`);
      load();
    } catch (e) {
      message.error(apiError(e, "Couldn't cancel this version"));
    }
  };

  const openEditor = (mode, policy = null) => setEditor({ open: true, mode, policy });

  const current = data?.current;
  const scheduled = (data?.versions || []).filter((v) => v.status === "SCHEDULED");

  const columns = [
    { title: "Version", dataIndex: "version", width: 80, render: (v) => `v${v}` },
    { title: "Mode", dataIndex: "billing_mode", render: (m) => MODE_LABELS[m] || m },
    { title: "Effective from", dataIndex: "effective_from", render: formatDateTime },
    { title: "Status", key: "status", render: (_, p) => <StatusTag policy={p} /> },
    { title: "Created by", dataIndex: "created_by", render: (u) => u?.name || "System" },
    {
      title: "",
      key: "actions",
      align: "right",
      render: (_, p) => (
        <Space size={4}>
          <Button size="small" icon={<ExperimentOutlined />} onClick={() => openEditor("view", p)}>
            View & test
          </Button>
          {p.status === "SCHEDULED" && (
            <>
              <Button size="small" onClick={() => openEditor("edit", p)}>Edit</Button>
              <Popconfirm
                title={`Cancel version ${p.version}?`}
                description="It won't take effect. This can't be undone."
                okText="Cancel version"
                cancelText="Keep"
                onConfirm={() => cancelPolicy(p)}
              >
                <Button size="small" danger>Cancel</Button>
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <div>
          <div className={styles.cardTitle}>Inpatient billing policies</div>
          <div className={styles.muted}>
            How bed and IP package days are counted, per facility. Changes are scheduled and never
            reprice existing admissions.
          </div>
        </div>
        <Space>
          <Select
            value={facilityId}
            options={facilities}
            onChange={setFacilityId}
            placeholder="Select facility"
            showSearch
            optionFilterProp="label"
            style={{ width: 240 }}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            disabled={!data}
            onClick={() => openEditor("create")}
          >
            New version
          </Button>
        </Space>
      </div>

      <Spin spinning={loading}>
        {!data ? (
          <div className={styles.card}>
            <Empty description={facilityId ? "No data" : "Select a facility"} />
          </div>
        ) : (
          <>
            {!data.facility?.dynamic_billing_enabled && (
              <Alert
                className={styles.notice}
                type="info"
                showIcon
                message="Calendar day and 24-hour modes aren't enabled for this facility yet. You can still configure and test them; only Standard can be scheduled."
              />
            )}

            <div className={styles.summaryGrid}>
              <div className={styles.card}>
                <div className={styles.cardLabel}>In force now</div>
                {current && (
                  <>
                    <div className={styles.cardHeadline}>
                      {MODE_LABELS[current.billing_mode]} <Tag>v{current.version}</Tag>
                    </div>
                    <div className={styles.muted} style={{ marginBottom: 8 }}>
                      {formatDateTime(current.effective_from)} · timezone {data.facility?.timezone}
                    </div>
                    <PolicyRules policy={current} />
                  </>
                )}
              </div>
              <div className={styles.card}>
                <div className={styles.cardLabel}>Scheduled</div>
                {scheduled.length ? (
                  scheduled
                    .slice()
                    .reverse()
                    .map((p) => (
                      <div key={p.id} className={styles.scheduledItem}>
                        <div className={styles.cardHeadline}>
                          {MODE_LABELS[p.billing_mode]} <Tag color="blue">v{p.version}</Tag>
                        </div>
                        <div className={styles.muted} style={{ marginBottom: 8 }}>
                          Takes effect {formatDateTime(p.effective_from)}
                        </div>
                        <PolicyRules policy={p} />
                      </div>
                    ))
                ) : (
                  <div className={styles.muted}>Nothing scheduled. The current policy stays in force.</div>
                )}
              </div>
            </div>

            <div className={styles.card}>
              <div className={styles.cardLabel}>History</div>
              <Table
                size="small"
                rowKey="id"
                columns={columns}
                dataSource={data.versions}
                pagination={false}
                scroll={{ x: 760 }}
              />
            </div>
          </>
        )}
      </Spin>

      <PolicyEditorModal
        open={editor.open}
        mode={editor.mode}
        policy={editor.policy}
        facility={data?.facility}
        currentPolicy={current}
        onClose={() => setEditor((e) => ({ ...e, open: false }))}
        onSaved={() => {
          setEditor((e) => ({ ...e, open: false }));
          load();
        }}
      />
    </div>
  );
};

export default BillingPolicies;
